import { useEffect, useRef, useState } from "react"
import { UseScriptOptions, UseScriptStatus } from "./useScript.domain"
import {
	getScriptNode,
	getStatusFromCacheOrDOM,
	getStatusFromEvent,
	isScriptFinished,
	isScriptReady,
} from "./useScript.utils"

const useScript = (src: string, options?: UseScriptOptions) => {
	const cachedScriptStatuses = useRef<
		Record<string, UseScriptStatus | undefined>
	>({})

	const [status, setStatus] = useState<UseScriptStatus>(() => {
		if (isScriptReady(src, options)) {
			return "idle"
		}

		return cachedScriptStatuses.current[src] ?? "loading"
	})

	useEffect(() => {
		const createScript = (cachedScriptStatus?: UseScriptStatus) => {
			const script = getScriptNode(src)

			let scriptNode = script.node

			if (!scriptNode) {
				scriptNode = document.createElement("script")
				scriptNode.src = src
				scriptNode.async = true
				scriptNode.setAttribute("data-status", "loading")
				document.body.appendChild(scriptNode)

				const setAttributeFromEvent = (event: Event) => {
					const scriptStatus = getStatusFromEvent(event.type)

					scriptNode?.setAttribute("data-status", scriptStatus)
				}

				scriptNode.addEventListener("load", setAttributeFromEvent)
				scriptNode.addEventListener("error", setAttributeFromEvent)
			} else {
				const currentStatus = getStatusFromCacheOrDOM(
					script.status,
					cachedScriptStatus
				)
				setStatus(currentStatus)
			}

			return scriptNode
		}

		if (isScriptReady(src, options)) {
			return () => undefined
		}

		const cachedScriptStatus = cachedScriptStatuses.current[src]

		if (isScriptFinished(cachedScriptStatus)) {
			setStatus(cachedScriptStatus)
			return () => undefined
		}

		const scriptNode = createScript(cachedScriptStatus)

		const setStateFromEvent = (event: Event) => {
			const newStatus = getStatusFromEvent(event.type)
			setStatus(newStatus)
			cachedScriptStatuses.current[src] = newStatus
		}

		scriptNode.addEventListener("load", setStateFromEvent)
		scriptNode.addEventListener("error", setStateFromEvent)

		return () => {
			if (!scriptNode) {
				return
			}

			scriptNode.removeEventListener("load", setStateFromEvent)
			scriptNode.removeEventListener("error", setStateFromEvent)

			if (options?.removeOnUnmount) {
				scriptNode.remove()
			}
		}
	}, [src, options?.shouldPreventLoad, options?.removeOnUnmount])

	return status
}

export default useScript
